import { useCallback, useLayoutEffect, useMemo } from 'react';
import type { WheelEvent, WheelEventHandler } from 'react';
import { cn } from '@/shared/utils/cn';
import { Text } from '@/modules/Text';
import {
  useGhanttConfig,
  ghanttBuildTimelineSlots,
  ghanttBuildUpperFloorSegments,
  ghanttDefaultColumnWidthForScale,
  GHANTT_TIMELINE_HEADER_STACK_HEIGHT_PX,
  GhanttSlotStrip,
  ghanttSnapSlotWidthPx,
  ghanttVerticalSlotGridBackgroundStyle,
  type GhanttTimelineScale,
  type GhanttTimelineSlot,
} from '@/shared/ghantt';

export interface GhanttTimelineHeaderProps {
  /**
   * Nombre accesible del bloque de cabecera (no dibuja una tercera franja: la cabecera son **dos pisos**
   * alineados al ancho de la columna).
   */
  title?: string;
  className?: string;
}

/**
 * Cabecera de la columna Gantt en **dos pisos** a ancho completo de la columna, alineados con el cuerpo:
 * - **Piso 1:** agrupación temporal (meses en vista diaria/semanal, años en vista mensual). En vista **año** no hay piso superior (solo años en el piso inferior).
 * - **Piso 2:** etiquetas de cada slot (días, semanas o meses según `timeline.scale`).
 *
 * Pensado para usarse como `column.header` de la columna `gantt` (el `HeaderCell` la renderiza a ancho
 * completo sin la fila de acciones de ordenar/filtrar).
 */
export function GhanttTimelineHeader({ title, className }: GhanttTimelineHeaderProps) {
  const cfg = useGhanttConfig();
  const scale = (cfg?.timeline.scale ?? 'day') as GhanttTimelineScale;
  const viewStart = cfg?.timeline.viewStart;
  const viewEnd = cfg?.timeline.viewEnd;
  const includeLeadingFullMonth = cfg?.timeline.includeLeadingFullMonth !== false;

  const slotsFromCtx = cfg?.timelineSlots;
  const slots = useMemo(() => {
    if (slotsFromCtx && slotsFromCtx.length > 0) {
      return slotsFromCtx;
    }
    if (
      viewStart === null ||
      viewStart === undefined ||
      viewEnd === null ||
      viewEnd === undefined
    ) {
      return [];
    }
    return ghanttBuildTimelineSlots(viewStart, viewEnd, scale, {
      includeLeadingFullMonth: scale === 'day' ? includeLeadingFullMonth : undefined,
    });
  }, [slotsFromCtx, viewStart, viewEnd, scale, includeLeadingFullMonth]);

  const upperSegments = useMemo(
    () => (slots.length > 0 ? ghanttBuildUpperFloorSegments(slots, scale) : []),
    [slots, scale]
  );

  const slotMinWidthPx = cfg?.timeline.columnWidthPx ?? ghanttDefaultColumnWidthForScale(scale);

  const snappedSlotPx =
    slotMinWidthPx !== null && slotMinWidthPx !== undefined && slotMinWidthPx > 0
      ? ghanttSnapSlotWidthPx(slotMinWidthPx)
      : undefined;

  const timelineInnerWidthPx =
    snappedSlotPx !== null && snappedSlotPx !== undefined && slots.length > 0
      ? slots.length * snappedSlotPx
      : undefined;
  const fixedDayColumns =
    slotMinWidthPx !== null &&
    slotMinWidthPx !== undefined &&
    slotMinWidthPx > 0 &&
    timelineInnerWidthPx !== null &&
    timelineInnerWidthPx !== undefined;

  const renderHeaderSlot = useCallback(
    (slot: GhanttTimelineSlot) => (
      <Text
        type="body"
        size="xs"
        weight="medium"
        color="secondary"
        className="ui-block ui-w-full ui-min-w-0 ui-truncate ui-text-center ui-tabular-nums"
      >
        {slot.label}
      </Text>
    ),
    []
  );

  const hs = cfg?.horizontalScroll;

  /** React marca `wheel` como pasivo en muchos casos; hace falta `passive: false` para `preventDefault` del zoom. */
  useLayoutEffect(() => {
    if (!cfg) return;
    const el = hs?.headerViewportRef?.current ?? null;
    const handler = hs?.onBodyWheel as WheelEventHandler<HTMLDivElement> | undefined;
    if (!el || !handler) return;
    const listener = (ev: Event) => {
      handler(ev as unknown as WheelEvent<HTMLDivElement>);
    };
    el.addEventListener('wheel', listener, { passive: false });
    return () => {
      el.removeEventListener('wheel', listener);
    };
  }, [cfg, hs?.headerViewportRef, hs?.onBodyWheel, timelineInnerWidthPx, slots.length]);

  if (!cfg) {
    return (
      <div className={cn('ui-flex ui-w-full ui-min-w-0 ui-flex-col', className)}>
        {title ? <span className="ui-sr-only">{title}</span> : null}
        <Text type="body" size="xs" color="secondary">
          —
        </Text>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'ui-box-border ui-flex ui-h-full ui-w-full ui-min-w-0 ui-shrink-0 ui-flex-col',
        className
      )}
      aria-label={title ?? 'Cronograma'}
    >
      {title ? <span className="ui-sr-only">{title}</span> : null}

      <div
        ref={hs?.headerViewportRef}
        onScroll={hs?.onHeaderViewportScroll}
        className="no-scrollbar ui-w-full ui-min-w-0 ui-overflow-x-auto ui-overflow-y-hidden"
      >
        <div
          className={cn(
            upperSegments.length > 0
              ? 'ui-grid ui-min-w-0 ui-shrink-0 ui-bg-white'
              : 'ui-flex ui-min-h-0 ui-w-full ui-min-w-0 ui-shrink-0 ui-flex-col ui-bg-white'
          )}
          style={{
            ...(timelineInnerWidthPx !== null && timelineInnerWidthPx !== undefined
              ? { width: timelineInnerWidthPx, minWidth: timelineInnerWidthPx }
              : { width: '100%' }),
            height: GHANTT_TIMELINE_HEADER_STACK_HEIGHT_PX,
            minHeight: GHANTT_TIMELINE_HEADER_STACK_HEIGHT_PX,
            ...(upperSegments.length > 0 ? { gridTemplateRows: '1fr 1fr' } : {}),
          }}
        >
          {upperSegments.length > 0 ? (
            <div className="ui-relative ui-flex ui-h-full ui-min-h-0 ui-w-full ui-overflow-hidden ui-border-b ui-border-default">
              {fixedDayColumns &&
              slots.length > 0 &&
              snappedSlotPx !== null &&
              snappedSlotPx !== undefined ? (
                <div
                  className="ui-pointer-events-none ui-absolute ui-inset-0 ui-z-0 ui-overflow-hidden"
                  aria-hidden
                >
                  <div
                    className="ui-h-full ui-shrink-0"
                    style={ghanttVerticalSlotGridBackgroundStyle(
                      slots.length,
                      snappedSlotPx,
                      upperSegments,
                      true
                    )}
                  />
                </div>
              ) : null}
              <div className="ui-relative ui-z-[1] ui-flex ui-h-full ui-min-h-0 ui-w-full">
                {upperSegments.map((seg, index) => {
                  let startIdx = 0;
                  for (let i = 0; i < index; i++) startIdx += upperSegments[i].slotSpan;

                  return (
                    <div
                      key={index}
                      className={cn(
                        'ui-absolute ui-top-0 ui-box-border ui-flex ui-h-full ui-min-h-0 ui-items-center ui-justify-center ui-px-0 ui-py-0',
                        fixedDayColumns ? 'ui-shrink-0' : 'ui-min-w-0'
                      )}
                      style={
                        fixedDayColumns && snappedSlotPx !== null && snappedSlotPx !== undefined
                          ? {
                              left: startIdx * snappedSlotPx,
                              width: seg.slotSpan * snappedSlotPx,
                            }
                          : {
                              left: `${(startIdx / slots.length) * 100}%`,
                              width: `${(seg.slotSpan / slots.length) * 100}%`,
                            }
                      }
                    >
                      <Text
                        type="body"
                        size="xs"
                        weight="medium"
                        color="secondary"
                        className="ui-line-clamp-1 ui-w-full ui-px-0.5 ui-text-center"
                      >
                        {seg.label}
                      </Text>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          <GhanttSlotStrip
            slots={slots}
            upperSegments={upperSegments}
            variant="header"
            className={
              upperSegments.length > 0
                ? 'ui-h-full ui-min-h-0 ui-min-w-0'
                : 'ui-min-h-0 ui-min-w-0 ui-flex-1'
            }
            slotMinWidthPx={slotMinWidthPx}
            renderSlot={renderHeaderSlot}
          />
        </div>
      </div>
    </div>
  );
}
